import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/ui-extra";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { Loader2, Users, ShieldCheck, Plus, Trash2, Crown, Copy, Eye, EyeOff, RefreshCw, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/ConfirmDialog";

export default function MembersAndRolesPage() {
  const { isMaster } = useAuth();
  return (
    <div className="space-y-6">
      <PageHeader
        title="Members & Roles"
        subtitle="Assign roles to dashboard users and configure per-role permissions"
      />
      {!isMaster ? (
        <Card><CardContent className="py-8 text-center text-muted-foreground">
          Master admins only.
        </CardContent></Card>
      ) : (
        <Tabs defaultValue="members">
          <TabsList>
            <TabsTrigger value="members" data-testid="tab-members">
              <Users className="h-4 w-4 mr-2" /> Members
            </TabsTrigger>
            <TabsTrigger value="roles" data-testid="tab-roles">
              <ShieldCheck className="h-4 w-4 mr-2" /> Roles & permissions
            </TabsTrigger>
          </TabsList>
          <TabsContent value="members" className="mt-4">
            <MembersPanel />
          </TabsContent>
          <TabsContent value="roles" className="mt-4">
            <RolesPanel />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Members panel — list of users + role assignment per row
// Also hosts the "Add member" dialog (creates a user in Clerk AND the
// local users table in one atomic call — no more "user has to sign in
// once before I can assign them a role") and per-row deletion.
// ────────────────────────────────────────────────────────────────────
function MembersPanel() {
  const { user: currentUser } = useAuth();
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  // Post-create credentials preview — a separate dialog surfaces the
  // username + password one last time so the admin can copy them to
  // share with the new teammate. Cleared as soon as the dialog closes.
  const [lastCreated, setLastCreated] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [m, r] = await Promise.all([api.get("/members"), api.get("/roles")]);
      setMembers(m.data);
      setRoles(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load members");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setRole = async (memberId, roleKey) => {
    setUpdatingId(memberId);
    try {
      await api.put(`/members/${memberId}/role`, { role_key: roleKey });
      toast.success("Role updated");
      setMembers((prev) => prev.map((m) => (m.id === memberId ? { ...m, role_key: roleKey } : m)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update role");
    } finally { setUpdatingId(null); }
  };

  const removeMember = async (member) => {
    setDeletingId(member.id);
    try {
      await api.delete(`/members/${member.id}`);
      toast.success(`Removed ${member.username}`);
      setMembers((prev) => prev.filter((m) => m.id !== member.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove member");
      throw e; // let ConfirmDialog keep itself open on failure
    } finally { setDeletingId(null); }
  };

  return (
    <>
      <Card className="rounded-xl overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3 px-4">
          <div>
            <CardTitle className="text-sm font-medium">Members</CardTitle>
            <p className="text-xs text-muted-foreground mt-0.5">
              Provision new teammates directly — they'll appear in Clerk and here at the same time.
            </p>
          </div>
          <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="add-member-button">
            <UserPlus className="h-4 w-4 mr-2" /> Add member
          </Button>
        </CardHeader>
        <div className="overflow-auto">
          <table className="w-full text-sm" data-testid="members-table">
            <thead className="bg-[hsl(var(--ap-paper))]">
              <tr className="text-xs uppercase tracking-[0.14em] text-muted-foreground border-b">
                <th className="text-left px-4 py-3">Username</th>
                <th className="text-left px-4 py-3">Display name</th>
                <th className="text-left px-4 py-3">Role</th>
                <th className="text-left px-4 py-3">Joined</th>
                <th className="text-right px-4 py-3 w-14"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="text-center py-10 text-muted-foreground">Loading…</td></tr>
              ) : members.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-12 text-muted-foreground">No members yet.</td></tr>
              ) : members.map((m) => {
                const isSelf = currentUser && (currentUser.id === m.id || currentUser.username === m.username);
                return (
                  <tr key={m.id} className="border-b hover:bg-accent/60" data-testid={`member-row-${m.id}`}>
                    <td className="px-4 py-3 font-medium">
                      <span className="flex items-center gap-2">
                        {m.is_master && (
                          <TooltipProvider><Tooltip>
                            <TooltipTrigger asChild>
                              <Crown className="h-3.5 w-3.5 text-[hsl(var(--ap-brass))]" />
                            </TooltipTrigger>
                            <TooltipContent>Master admin. Role can't be changed here.</TooltipContent>
                          </Tooltip></TooltipProvider>
                        )}
                        {m.username || <span className="text-muted-foreground">—</span>}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{m.name || ""}</td>
                    <td className="px-4 py-3">
                      {m.is_master ? (
                        <span className="rounded-full bg-[hsl(var(--ap-brass)/0.18)] text-[hsl(var(--ap-brass))] text-xs px-2.5 py-1">Master admin</span>
                      ) : (
                        <Select
                          value={m.role_key}
                          onValueChange={(v) => setRole(m.id, v)}
                          disabled={updatingId === m.id}
                        >
                          <SelectTrigger className="h-8 w-44" data-testid={`member-role-select-${m.id}`}>
                            {updatingId === m.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <SelectValue />}
                          </SelectTrigger>
                          <SelectContent>
                            {roles
                              .filter((r) => r.key !== "master_admin")
                              .map((r) => (
                                <SelectItem key={r.key} value={r.key} data-testid={`role-option-${r.key}`}>
                                  {r.name}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground font-mono">
                      {m.linked_at ? m.linked_at.slice(0, 10) : (m.created_at ? m.created_at.slice(0, 10) : "")}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {/* Delete is hidden for master admins (env-controlled) and
                          for the current user (safety — no self-lockouts). */}
                      {!m.is_master && !isSelf && (
                        <ConfirmDialog
                          title={`Remove ${m.username}?`}
                          description={
                            <>
                              <p>
                                This deletes <span className="font-medium text-foreground">{m.username}</span> from
                                both Clerk and this platform. Their sessions are invalidated immediately and they
                                will no longer be able to sign in.
                              </p>
                              <p className="mt-2 text-[hsl(var(--destructive))] font-medium">This is irreversible.</p>
                            </>
                          }
                          confirmLabel="Yes, remove member"
                          onConfirm={() => removeMember(m)}
                          testId={`confirm-remove-member-${m.id}`}
                        >
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground hover:text-destructive h-8 w-8"
                            disabled={deletingId === m.id}
                            data-testid={`remove-member-${m.id}`}
                          >
                            {deletingId === m.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        </ConfirmDialog>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <CreateMemberDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        roles={roles}
        onCreated={(m, credentials) => {
          setMembers((prev) => [...prev, m]);
          setLastCreated(credentials);
        }}
      />

      <CreatedCredentialsDialog
        credentials={lastCreated}
        onClose={() => setLastCreated(null)}
      />
    </>
  );
}


// ────────────────────────────────────────────────────────────────────
// Create-member dialog
// ────────────────────────────────────────────────────────────────────
// Generates a strong random password client-side and lets the admin
// tweak it if desired. On submit, POSTs to /api/members which handles
// both the Clerk provisioning and the local mirror insert. Any Clerk
// error (weak password, username taken) is surfaced verbatim so the
// admin can react (e.g. try a stronger password).
// ────────────────────────────────────────────────────────────────────

// 16 random URL-safe chars ≈ 96 bits of entropy — well above Clerk's
// minimums and HIBP-resistant. Uses window.crypto so it's actually
// random, not Math.random.
function generateStrongPassword(len = 16) {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%^&*";
  const buf = new Uint32Array(len);
  window.crypto.getRandomValues(buf);
  let out = "";
  for (let i = 0; i < len; i++) out += alphabet[buf[i] % alphabet.length];
  return out;
}

function CreateMemberDialog({ open, onOpenChange, roles, onCreated }) {
  const [username, setUsername] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [roleKey, setRoleKey] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Default to a sensible non-master role the first time the dialog
  // opens, then remember whatever the admin last picked.
  useEffect(() => {
    if (!open) return;
    // Reset form each time we open, but keep a strong password
    // pre-filled so the admin only has to fill username + names.
    setUsername("");
    setFirstName("");
    setLastName("");
    setPassword(generateStrongPassword());
    setShowPassword(false);
    if (!roleKey && roles.length) {
      // Prefer 'executive' as the default (matches the auto-provision
      // default in clerk_auth); fall back to whatever comes first.
      const preferred = roles.find((r) => r.key === "executive") || roles.find((r) => r.key !== "master_admin");
      setRoleKey(preferred?.key || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async () => {
    const u = username.trim();
    if (!u || u.length < 3) { toast.error("Username must be at least 3 characters"); return; }
    if (!/^[a-zA-Z0-9_.-]+$/.test(u)) { toast.error("Username may only contain letters, numbers, and . _ -"); return; }
    if (!password || password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (!roleKey) { toast.error("Pick a role"); return; }

    setSubmitting(true);
    try {
      const res = await api.post("/members", {
        username: u,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        password,
        role_key: roleKey,
      });
      toast.success(`Created ${res.data.username}`);
      onCreated(res.data, { username: u, password });
      onOpenChange(false);
    } catch (e) {
      // Clerk error messages come through verbatim (e.g. "password is
      // in a list of leaked passwords"). Preserve them for the admin.
      toast.error(e?.response?.data?.detail || "Failed to create member");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-member-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add member</DialogTitle>
          <p className="text-xs text-muted-foreground pt-1">
            Creates the account in Clerk and stamps their role here in one step. Share the credentials with them
            out-of-band; they can change the password from their profile after signing in.
          </p>
        </DialogHeader>

        <div className="space-y-3 pt-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>First name</Label>
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="e.g. Ada"
                data-testid="create-member-first-name"
              />
            </div>
            <div>
              <Label>Last name</Label>
              <Input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="e.g. Lovelace"
                data-testid="create-member-last-name"
              />
            </div>
          </div>
          <div>
            <Label>Username</Label>
            <Input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="e.g. ada.lovelace"
              autoComplete="off"
              data-testid="create-member-username"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Letters, numbers, and <code>. _ -</code> only. Case-insensitive on sign-in.
            </p>
          </div>
          <div>
            <Label>Temporary password</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pr-9 font-mono text-sm"
                  autoComplete="new-password"
                  data-testid="create-member-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  data-testid="create-member-toggle-password"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => { setPassword(generateStrongPassword()); setShowPassword(true); }}
                title="Generate a new strong password"
                data-testid="create-member-regen-password"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">
              Clerk enforces strength + checks against leaked-password lists. Share this password with the user; they
              can change it from their profile.
            </p>
          </div>
          <div>
            <Label>Role</Label>
            <Select value={roleKey} onValueChange={setRoleKey}>
              <SelectTrigger data-testid="create-member-role"><SelectValue placeholder="Pick a role" /></SelectTrigger>
              <SelectContent>
                {roles
                  .filter((r) => r.key !== "master_admin")
                  .map((r) => (
                    <SelectItem key={r.key} value={r.key} data-testid={`create-member-role-option-${r.key}`}>
                      {r.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground mt-1">
              Master admin can't be granted here — it's controlled by the <code>MASTER_ADMIN_USERNAMES</code> env var.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="create-member-submit">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Create member
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ────────────────────────────────────────────────────────────────────
// Post-create dialog — surfaces the credentials one last time so the
// admin can copy them out before they're wiped from state. We DON'T
// persist them anywhere; closing the dialog is permanent.
// ────────────────────────────────────────────────────────────────────
function CreatedCredentialsDialog({ credentials, onClose }) {
  const open = !!credentials;
  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Clipboard not available");
    }
  };
  const copyBoth = async () => {
    if (!credentials) return;
    const combined = `Username: ${credentials.username}\nPassword: ${credentials.password}`;
    await copy(combined, "Credentials");
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="created-credentials-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Share these credentials</DialogTitle>
          <p className="text-xs text-muted-foreground pt-1">
            This is the last time the password is shown. Copy it now and send it to the new member securely
            (e.g. via a password manager or an encrypted message).
          </p>
        </DialogHeader>
        {credentials && (
          <div className="space-y-3 pt-1">
            <div>
              <Label>Username</Label>
              <div className="flex gap-2">
                <Input value={credentials.username} readOnly className="font-mono" data-testid="new-credentials-username" />
                <Button variant="outline" size="icon" onClick={() => copy(credentials.username, "Username")}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div>
              <Label>Password</Label>
              <div className="flex gap-2">
                <Input value={credentials.password} readOnly className="font-mono" data-testid="new-credentials-password" />
                <Button variant="outline" size="icon" onClick={() => copy(credentials.password, "Password")}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={copyBoth} data-testid="copy-credentials-both">
            <Copy className="h-4 w-4 mr-2" /> Copy username + password
          </Button>
          <Button onClick={onClose} data-testid="close-credentials-dialog">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────
// Roles panel — left: list of roles, right: permission checkboxes
// ────────────────────────────────────────────────────────────────────
function RolesPanel() {
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState({}); // {category: {key: description}}
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState(null);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newRoleOpen, setNewRoleOpen] = useState(false);
  const [newRole, setNewRole] = useState({ key: "", name: "", description: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([api.get("/roles"), api.get("/permissions/catalog")]);
      setRoles(r.data);
      setCatalog(c.data.catalog || {});
      if (!selectedKey && r.data.length) setSelectedKey(r.data[0].key);
    } catch (e) {
      toast.error("Could not load roles");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const selectedRole = useMemo(
    () => roles.find((r) => r.key === selectedKey) || null,
    [roles, selectedKey]
  );

  const togglePermission = (permKey) => {
    if (!selectedRole) return;
    if (selectedRole.key === "master_admin") return; // immutable
    const has = (selectedRole.permissions || []).includes(permKey);
    const next = has
      ? (selectedRole.permissions || []).filter((p) => p !== permKey)
      : [...(selectedRole.permissions || []), permKey];
    setRoles((prev) => prev.map((r) => (r.key === selectedRole.key ? { ...r, permissions: next } : r)));
  };

  const savePermissions = async () => {
    if (!selectedRole) return;
    setSaving(true);
    try {
      const res = await api.put(`/roles/${selectedRole.key}`, {
        permissions: selectedRole.permissions,
      });
      toast.success(`${selectedRole.name} updated`);
      setRoles((prev) => prev.map((r) => (r.key === selectedRole.key ? res.data : r)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const createRole = async () => {
    if (!newRole.key || !newRole.name) { toast.error("Key and name required"); return; }
    setCreating(true);
    try {
      const res = await api.post("/roles", { ...newRole, permissions: [] });
      toast.success(`Role ${res.data.name} created`);
      setRoles((prev) => [...prev, res.data]);
      setSelectedKey(res.data.key);
      setNewRoleOpen(false);
      setNewRole({ key: "", name: "", description: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create role");
    } finally { setCreating(false); }
  };

  const deleteRole = async (role) => {
    if (role.is_system) { toast.error("System role cannot be deleted"); return; }
    try {
      await api.delete(`/roles/${role.key}`);
      toast.success(`Deleted ${role.name}`);
      setRoles((prev) => prev.filter((r) => r.key !== role.key));
      if (selectedKey === role.key) setSelectedKey(roles[0]?.key || null);
    } catch (e) {
      toast.error("Failed to delete");
      throw e;
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4">
      {/* Roles list */}
      <Card className="rounded-xl">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Roles</CardTitle>
          <Dialog open={newRoleOpen} onOpenChange={setNewRoleOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="ghost" data-testid="new-role-button"><Plus className="h-3.5 w-3.5" /></Button>
            </DialogTrigger>
            <DialogContent data-testid="new-role-dialog">
              <DialogHeader><DialogTitle>Create role</DialogTitle></DialogHeader>
              <div className="space-y-3 pt-2">
                <div>
                  <Label>Key (snake_case)</Label>
                  <Input value={newRole.key}
                         onChange={(e) => setNewRole({ ...newRole, key: e.target.value })}
                         placeholder="e.g. finance_lead" data-testid="new-role-key" />
                </div>
                <div>
                  <Label>Display name</Label>
                  <Input value={newRole.name}
                         onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
                         placeholder="e.g. Finance Lead" data-testid="new-role-name" />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea value={newRole.description}
                            onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
                            placeholder="What this role can do" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setNewRoleOpen(false)}>Cancel</Button>
                <Button onClick={createRole} disabled={creating} data-testid="new-role-submit">
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent className="px-2 py-1 space-y-0.5">
          {loading ? (
            <div className="text-sm text-muted-foreground p-4">Loading…</div>
          ) : roles.map((r) => (
            <button
              key={r.key}
              onClick={() => setSelectedKey(r.key)}
              data-testid={`role-list-${r.key}`}
              className={
                "w-full text-left px-3 py-2 rounded-md flex items-center justify-between " +
                (selectedKey === r.key ? "bg-accent" : "hover:bg-accent/60")
              }
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate flex items-center gap-1">
                  {r.key === "master_admin" && <Crown className="h-3 w-3 text-[hsl(var(--ap-brass))]" />}
                  {r.name}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {r.member_count} member{r.member_count === 1 ? "" : "s"} ·{" "}
                  {r.key === "master_admin" ? "all permissions" : `${(r.permissions || []).length} perms`}
                  {r.is_system ? " · system" : ""}
                </div>
              </div>
            </button>
          ))}
        </CardContent>
      </Card>

      {/* Permissions editor */}
      <Card className="rounded-xl">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div>
            <CardTitle className="text-base">
              {selectedRole ? selectedRole.name : "Pick a role"}
            </CardTitle>
            {selectedRole?.description && (
              <p className="text-xs text-muted-foreground mt-0.5">{selectedRole.description}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {selectedRole && !selectedRole.is_system && (
              <ConfirmDialog
                title={`Delete role "${selectedRole.name}"?`}
                description={
                  <>
                    <p>Every user currently assigned to <span className="font-medium text-foreground">{selectedRole.name}</span> will be automatically moved to <span className="font-medium text-foreground">Executive</span> and may temporarily gain or lose specific permissions as a result.</p>
                    <p className="mt-2 text-[hsl(var(--destructive))] font-medium">The role, its permission set, and any customisations you made to it will be permanently removed.</p>
                  </>
                }
                confirmLabel="Yes, delete role"
                onConfirm={() => deleteRole(selectedRole)}
                testId={`delete-role-${selectedRole.key}`}
              >
                <Button variant="ghost" size="icon"
                        data-testid={`delete-role-${selectedRole.key}`}
                        className="text-muted-foreground hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </ConfirmDialog>
            )}
            {selectedRole && selectedRole.key !== "master_admin" && (
              <Button onClick={savePermissions} disabled={saving} size="sm" data-testid="save-role-button">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Save
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {!selectedRole ? (
            <div className="text-sm text-muted-foreground py-6 text-center">Select a role on the left.</div>
          ) : selectedRole.key === "master_admin" ? (
            <div className="text-sm text-muted-foreground py-4">
              The master admin role bypasses all permission checks.
              Its membership is controlled by the <code>MASTER_ADMIN_USERNAMES</code> env var.
            </div>
          ) : (
            Object.entries(catalog).map(([category, keys]) => (
              <div key={category}>
                <div className="text-xs uppercase tracking-[0.14em] text-muted-foreground mb-2">
                  {category}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {Object.entries(keys).map(([permKey, desc]) => {
                    const checked = (selectedRole.permissions || []).includes(permKey);
                    return (
                      <label
                        key={permKey}
                        className="flex items-start gap-2 p-2 rounded-md hover:bg-accent/60 cursor-pointer"
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => togglePermission(permKey)}
                          data-testid={`perm-${selectedRole.key}-${permKey}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium font-mono">{permKey}</div>
                          <div className="text-xs text-muted-foreground">{desc}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
